import React, { useState, useEffect, useMemo } from 'react';

import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { View } from 'components/layout/View';
import Text from 'components/outputs/Text';
import { Button } from 'components/inputs/Button';
import Spinner from 'components/outputs/Spinner';
import Image from 'components/outputs/Image';
import * as Inputs from 'config/inputs';
import Input from 'components/inputs/Input';
import MobileInput from 'components/inputs/PhoneInput';
import { validateMobileOld as validateMobile, isValidMobile } from 'util/validation';
import {
  createMFAAuthenticator,
  verifyMFA,
  getMFAAuthenticators,
  deleteMfaAuthenticator,
  getCompany,
} from 'util/rehive';
import { useToast } from 'components/contexts/ToastContext';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { configSettingsSelector } from 'redux/rehive/selectors';
import { currentCompanySupportedAuthenticatorsSelector } from 'redux/auth/selectors';
import {
  getMfaAuthenticatorVisibility,
  getSoleMfaAuthenticator,
  formatMfaError as formatMfaErrorMessage,
} from 'util/mfa';
import OutputList from 'components/lists/OutputList';
import { useMfaSuccess } from 'hooks/useMfaSuccess';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import Skeleton from '@material-ui/lab/Skeleton';
import Collapse from '@material-ui/core/Collapse';
import Fade from '@material-ui/core/Fade';
import StepHeading from 'components/outputs/StepHeading';

export default function MfaSetForm(props) {
  const {
    onSuccess,
    onSkip,
    tempAuth,
    setTempAuth,
    authConfig,
    descriptionId,
  } = props;
  const { t } = useTranslation('common');
  const skip = authConfig?.mfa === 'optional';
  const { showToast } = useToast();
  const settingsConfig = useSelector(configSettingsSelector);
  const supported = useSelector(currentCompanySupportedAuthenticatorsSelector);
  const [imageSrc, setImageSrc] = useState();

  const [state, setState] = useState('');
  const [error, setError] = useState('');
  const [token, setToken] = useState(null);
  const [sms, setSms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMobileValid, setIsMobileValid] = useState(false);
  const [showManualKey, setShowManualKey] = useState(false);

  const { data, isLoading, refetch } = useQuery(
    ['mfa', tempAuth?.token],
    () => getMFAAuthenticators(),
    {
      enabled: Boolean(authConfig.mfa),
    },
  );
  // During the temp-auth onboarding path the authenticated company may not be in
  // redux yet; fetch it directly so the supported authenticators are accurate.
  const { data: companyResp, isError: companyRespError } = useQuery(
    ['company', tempAuth?.token],
    () => getCompany(),
    { enabled: supported === undefined && Boolean(authConfig.mfa), staleTime: 5 * 60 * 1000 },
  );
  const supportedAuthenticators =
    supported === undefined
      ? companyResp?.settings?.supported_authenticators
      : supported;
  // With a single available method the selection screen never shows, so
  // back/cancel buttons must not route to it either.
  const sole = getSoleMfaAuthenticator(
    supportedAuthenticators,
    settingsConfig.hideSmsMfa,
  );

  const authenticators = data?.data;
  const verifiedAuthenticator = useMemo(
    () => authenticators?.results?.find(item => item.verified === true),
    [data],
  );
  const unverifiedAuthenticators = useMemo(
    () =>
      authenticators?.results?.filter(
        item => item.verified === false && item.id !== token?.id,
      ),
    [authenticators],
  );

  useEffect(() => {
    // removing unverified authenticators, otherwise new authenticator can't be set
    if (unverifiedAuthenticators?.length) {
      Promise.all(
        unverifiedAuthenticators.map(item => deleteMfaAuthenticator(item.id))
      ).catch(error => {
        console.error('Error deleting unverified authenticators:', error);
      });
    }
  }, [unverifiedAuthenticators]);

  const { setMfaStepCompleted } = useMfaSuccess({
    tempAuth,
    setTempAuth,
    onSuccess,
  });

  useEffect(() => {
    if (!authConfig?.mfa) setMfaStepCompleted(true);
  }, []);

  useEffect(() => {
    if (verifiedAuthenticator) setMfaStepCompleted(true);
  }, [verifiedAuthenticator]);

  useEffect(() => {
    // Decide the initial screen once the supported-authenticators data has
    // resolved (renderRouter shows a spinner until then, so the selection
    // screen never flashes). When exactly one MFA method is available, skip
    // the single-button selection screen and go straight to setup. Only from
    // the pristine initial state (an explicit 'back' sets 'landing', not ''),
    // and only when not already verified. A failed company fetch counts as
    // resolved so the screen stays usable (defaults to the selection screen).
    if (state !== '' || isLoading || verifiedAuthenticator) return;
    const supportedResolved =
      supported !== undefined || companyResp !== undefined || companyRespError;
    if (!supportedResolved) return;
    if (sole === 'totp') {
      setState('token');
      setLoading(true);
      getToken();
    } else if (sole === 'sms') {
      setState('sms');
      setIsMobileValid(false);
    } else {
      setState('landing');
    }
  }, [state, isLoading, verifiedAuthenticator, supported, companyResp, companyRespError, supportedAuthenticators]);

  useEffect(() => {
    if (!token?.details) return;
    const { issuer, account, key } = token.details;
    const url =
      'otpauth://totp/' +
      issuer +
      ':' +
      account +
      '?secret=' +
      key +
      '&digits=6&issuer=' +
      issuer;
    QRCode.toDataURL(url)
      .then(res => {
        setImageSrc(res);
      })
      .catch(err => {
        console.error(err);
      });
  }, [token]);

  function formatMfaError(message) {
    return formatMfaErrorMessage(message, t);
  }

  async function getToken() {
    if (!token) {
      setError('');
      try {
        // A leftover unverified TOTP authenticator (an abandoned earlier
        // setup) makes creation fail with "already exists" — remove any
        // before creating, rather than racing the unawaited cleanup effect
        // above (which may be deleting the same records concurrently).
        const staleTotp =
          unverifiedAuthenticators?.filter(item => item.type === 'totp') ?? [];
        await Promise.all(
          staleTotp.map(item => deleteMfaAuthenticator(item.id).catch(() => {})),
        );
        const _token = await createMFAAuthenticator('totp');
        if (_token?.details) {
          setToken(_token);
        } else {
          // callApi resolves API errors (throttled, already exists) as an
          // { status, message } envelope instead of rejecting.
          setError(formatMfaError(_token?.message));
        }
      } catch (e) {
        setError(formatMfaError(e.message));
      }
    }
    setLoading(false);
  }


  async function sendSMS({ setSubmitting, setFieldError, values }) {
    setSubmitting(true);
    try {
      const mobile = values.number;
      if (mobile !== sms?.details?.mobile) {
        if (sms) {
          await deleteMfaAuthenticator(sms.id);
        }
        const _sms = await createMFAAuthenticator('sms', { mobile });
        if (!_sms?.id) {
          // callApi resolves API errors (throttled, already exists) as an
          // { status, message } envelope instead of rejecting.
          throw new Error(
            formatMfaError(_sms?.message) ||
              t('unable_to_use_mfa', 'Unable to use multifactor authentication'),
          );
        }
        setSms(_sms);
      }
      setState('verifySMS');
    } catch (e) {
      setFieldError('number', e.message);
    }
    setSubmitting(false);
  }

  async function enableAuth({ setSubmitting, setFieldError, values }) {
    setSubmitting(true);
    try {
      const resp = await verifyMFA({
        token: values.otp,
        authenticator: token?.id ?? sms?.id,
      });
      if (resp?.status === 'success') {
        showToast({
          text: 'mfa_successfully_enabled',
          variant: 'success',
        });
        await refetch();
      } else {
        throw new Error(formatMfaError(resp?.message) || 'Invalid OTP');
      }
    } catch (e) {
      setFieldError('otp', e.message);
    }
    setSubmitting(false);
  }

  const handleMobileValidityChange = (isValid) => {
    setIsMobileValid(isValid);
  };

  // Optional-MFA skip: hosts that need to persist the choice (the post-onboarding
  // /mfa-setup/ gate) pass onSkip; otherwise skipping just completes the step.
  const handleSkip = () => {
    if (onSkip) onSkip();
    else setMfaStepCompleted(true);
  };

  const handleRetryToken = () => {
    setLoading(true);
    getToken();
  };

  const handleToggleManualKey = () => {
    setShowManualKey(prev => !prev);
  };

  function handleButton(type, formikProps) {
    switch (type) {
      case 'sms':
        setState('sms');
        setIsMobileValid(false);
        resetForm(formikProps);
        break;
      case 'token':
        setState('token');
        setLoading(true);
        resetForm(formikProps);
        getToken();
        break;
      case 'back':
      default:
        setState('landing');
    }
  }

  function resetForm({ setFieldValue, setFieldTouched }) {
    setFieldValue('otp', '');
    setFieldTouched('otp', false);
    setFieldValue('number', '');
    setFieldTouched('number', false);
  }

  function renderLanding(formikProps) {
    const { hideSmsMfa } = settingsConfig;
    const { showTotp, showSms } = getMfaAuthenticatorVisibility(
      supportedAuthenticators,
      hideSmsMfa,
    );

    return (
      <React.Fragment>
        {/* The contextual line above the router already explains why MFA setup
            is shown, so only prompt for the method choice here. */}
        <View mb={1}>
          <Text align="center" id="mfa_choose_method_description" />
        </View>

        {showTotp && (
          <Button
            label="mfa_method_token"
            capitalize
            noPadding
            color="primary"
            wide
            onPress={() => {
              handleButton('token', formikProps);
            }}
          />
        )}

        {showSms && (
          <View pt={1} w="100%">
            <Button
              label="mfa_method_sms"
              capitalize
              color="primary"
              variant="outlined"
              noPadding
              wide
              onPress={() => handleButton('sms', formikProps)}
            />
          </View>
        )}

        {skip && (
          <View w="100%" pt={1}>
            <Button
              label="skip"
              variant="text"
              noPadding
              color="primary"
              wide
              onPress={handleSkip}
            />
          </View>
        )}
      </React.Fragment>
    );
  }


  function renderToken(formikProps) {
    if (!token) {
      return (
        <View ph={0.5} pb={2} w={'100%'}>
          {error ? (
            <Text p={1} c={'error'} tA={'center'}>
              {error}
            </Text>
          ) : (
            <Text align="center" id="unable_to_use_mfa" />
          )}
          <View pt={1} w="100%">
            <Button
              label="retry"
              capitalize
              color="primary"
              noPadding
              wide
              onPress={handleRetryToken}
            />
          </View>
          {!sole && (
            <View pt={1} w="100%">
              <Button
                label="back"
                color="primary"
                variant="text"
                noPadding
                wide
                onPress={() => handleButton('back')}
              />
            </View>
          )}
          {sole && skip && (
            <View pt={1} w="100%">
              <Button
                label="skip"
                color="primary"
                variant="text"
                noPadding
                wide
                onPress={handleSkip}
              />
            </View>
          )}
        </View>
      );
    }
    const { issuer, account, key } = token.details;
    const outputs = [
      { label: 'issuer', value: issuer, copy: true },
      { label: 'account', value: account, copy: true },
      { label: 'key', value: key, copy: true },
    ];

    return (
      <React.Fragment>
        <View w={'100%'} pb={1}>
          <StepHeading number="1" id="mfa_totp_step_scan_title" />
          <View pt={0.5} w={'100%'}>
            <Text id="mfa_totp_step_scan_description" />
          </View>
          {/* White tile keeps the QR scannable on tenant themes with dark or
              tinted surfaces, and groups it with its manual-entry fallback. */}
          <View
            mt={1}
            w={'100%'}
            aI={'center'}
            bR={16}
            bC={'#FFFFFF'}
            border
            borderColor={'grey2'}
            pv={0.5}>
            {imageSrc ? (
              <Fade in timeout={200}>
                <div>
                  <Image
                    alt={t('mfa_totp_qr_alt')}
                    width={160}
                    height={160}
                    src={imageSrc}
                  />
                </div>
              </Fade>
            ) : (
              <Skeleton variant="rect" width={160} height={160} />
            )}
            <Button
              label="mfa_totp_cant_scan"
              color="primary"
              variant="text"
              noPadding
              style={{ textDecoration: 'underline' }}
              wrapperStyle={{ padding: 0 }}
              aria-expanded={showManualKey}
              aria-controls="mfa-totp-manual-key"
              onPress={handleToggleManualKey}
            />
            {/* The tile centres its children, so the Collapse would otherwise
                shrink to its content width and the divider below would span
                only part of the tile. */}
            <Collapse
              in={showManualKey}
              timeout={200}
              style={{ width: '100%' }}>
              <View ph={1} pb={0.5} w={'100%'} id="mfa-totp-manual-key">
                <View h={1} w={'100%'} bC={'grey2'} />
                <View pt={1} w={'100%'}>
                  <Text s={14} id="mfa_totp_manual_description" />
                </View>
                <View pt={0.5} w={'100%'}>
                  <OutputList
                    items={outputs}
                    outputProps={{
                      copy: true,
                    }}
                  />
                </View>
              </View>
            </Collapse>
          </View>

          <View pt={1} w={'100%'}>
            <StepHeading number="2" id="mfa_totp_step_code_title" />
          </View>
          <View pt={0.5} pb={0.5} w={'100%'}>
            <Text id="mfa_totp_step_code_description" />
          </View>
          {error ? (
            <Text p={1} c={'error'} tA={'center'} role="alert">
              {error}
            </Text>
          ) : null}
          {renderOTP(formikProps)}
        </View>
        {!sole && (
          <Button
            label="cancel"
            color="primary"
            variant="text"
            noPadding
            wide
            onPress={() => handleButton('back')}
          />
        )}
        {sole && skip && (
          <Button
            label="skip"
            color="primary"
            variant="text"
            noPadding
            wide
            onPress={handleSkip}
          />
        )}
      </React.Fragment>
    );
  }

  function renderOTP(formikProps) {
    const { values } = formikProps;
    return (
      <View w={'100%'}>
        <Input
          fullWidth
          field={Inputs.otp}
          value={values.otp}
          formikProps={formikProps}
        />
        <View pt={1} pb={0.5} w="100%">
          <Button
            label="submit"
            capitalize
            wide
            type="submit"
            // size="large"
            noPadding
            color="primary"
            disabled={formikProps.isSubmitting || !formikProps.isValid}
            loading={formikProps.isSubmitting}
            onPress={() => enableAuth(formikProps)}
          />
        </View>
      </View>
    );
  }

  function renderSMS(formikProps) {
    const { values, errors, touched, isSubmitting, setFieldValue, setFieldTouched } = formikProps;
    const helperTextString = t(Inputs.mobile_mfa.helper ?? 'mobile_helper');

    return (
      <View aI={'center'} w={'100%'}>
        <View pb={1} aI={'center'} w={'100%'}>
          <Text align={'center'} id="mfa_sms_description" />
        </View>
        <View w={'100%'} aI={'center'}>
          <View aI={'center'} w={'100%'}>
            <MobileInput
              existing={values.number}
              value={values.number}
              onChange={value => {
                setFieldValue('number', value);
              }}
              setFieldTouched={() => setFieldTouched('number', true)}
              setIsValidNumber={handleMobileValidityChange}
              error={Boolean(touched.number && errors.number)}
              helperText={touched.number && errors.number ? errors.number : helperTextString}
            />
          </View>
          <View aI={'center'} pt={1} w={'100%'}>
            <Button
              label="send_sms"
              capitalize
              color="primary"
              wide
              type="submit"
              noPadding
              disabled={isSubmitting || !isMobileValid}
              loading={isSubmitting}
              onPress={() => sendSMS(formikProps)}
            />
            {!sole && (
              <View pt={1} w="100%">
                <Button
                  noPadding
                  label="back"
                  color="primary"
                  variant="text"
                  wide
                  onPress={() => handleButton('back')}
                />
              </View>
            )}
            {sole && skip && (
              <View pt={1} w="100%">
                <Button
                  noPadding
                  label="skip"
                  color="primary"
                  variant="text"
                  wide
                  onPress={handleSkip}
                />
              </View>
            )}
          </View>
        </View>
      </View>
    );
  }

  function renderVerifySMS(formikProps) {
    const { values } = formikProps;
    return (
      <React.Fragment>
        <View aI={'center'} w={'100%'}>
          <Text
            align={'center'}
            p={0.5}
            id="please_enter_number"
            context={{ number: values.number }}
          />
          <View w={'100%'} aI={'center'}>
            {renderOTP(formikProps)}
          </View>
        </View>
        <Button
          wide
          noPadding
          color="primary"
          label="cancel"
          variant="text"
          onPress={handleCancelVerifySMS}
        />
      </React.Fragment>
    );
  }

  const handleCancelVerifySMS = () => {
    // With a single method there is no selection screen to go back to —
    // return to the number-entry screen instead.
    if (sole === 'sms') {
      setState('sms');
    } else {
      handleButton('back');
    }
  };

  function renderRouter(formikProps) {
    // During auth flow, don't show the deletion screen - the useEffect will handle completion
    if (Boolean(verifiedAuthenticator)) {
      // Show a loading state while the useEffect processes the completion
      return <Spinner />;
    }

    switch (state) {
      case 'token':
        return renderToken(formikProps);
      case 'sms':
        return renderSMS(formikProps);
      case 'verifySMS':
        return renderVerifySMS(formikProps);
      case '':
        // The initial-screen decision (single-method auto-skip) hasn't run
        // yet — don't flash the selection screen.
        return <Spinner />;
      default:
        return renderLanding(formikProps);
    }
  }

  function validation(values) {
    if (state.match(/token|verifySMS/)) {
      const schema = yup.object().shape({
        otp: yup
          .string()
          .required('OTP is required')
          .matches(/^\d{5,6}$/, 'OTP must be 5 or 6 digits')
          .test(
            'len',
            'OTP must be 5 or 6 digits',
            val => val && (val.length === 5 || val.length === 6),
          ),
      });
      let errors = {};
      try {
        errors = schema.validateSync(values);
      } catch (e) {
        errors = e;
      }
      if (errors.path) {
        return {
          [errors.path]: errors.message,
        };
      }
    } else if (state === 'sms') {
      if (!values.number || values.number.indexOf('+') === -1) {
        return { number: t('include_country_code_error', 'Please include a country code starting with +') };
      }
      const isValid = isValidMobile(values.number);
      if (!isValid) {
        return { number: t('valid_mobile_number_error', 'Please enter a valid mobile number') };
      }
    }
  }

  return (
    <Formik
      initialValues={{
        otp: '',
        number: '',
      }}
      validate={validation}
      validateOnChange={true}
      validateOnBlur={true}
      >
      {formikProps =>
        isLoading || loading ? (
          <Spinner />
        ) : (
          <>
            {authConfig?.mfa === '' ? (
              <div></div>
            ) : (
              <Form style={{ width: '100%' }}>
                {!verifiedAuthenticator && (
                  <View mb={2} ph={0.5}>
                    <Text
                      align="center"
                      c={'fontLight'}
                      id={
                        descriptionId ??
                        (tempAuth?.register
                          ? 'mfa_auth_description_register'
                          : 'mfa_auth_description_login')
                      }
                    />
                  </View>
                )}
                {renderRouter(formikProps)}
              </Form>
            )}
          </>
        )
      }
    </Formik>
  );
}
